import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  HairlineCard,
  Stack,
  useSnackbar,
} from '@munifocus/ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type {
  ExtractionMismatch,
  IntakeCheckFinding,
  OwnerType,
  SnbDocumentKind,
  TombstoneDraft,
} from '@munifocus/contracts-gov';
import {
  CONTACT_CHANNEL_INTAKE_METHOD,
  bylawCategoryForReport,
  countExtractedFields,
  intakeCheckKey,
  missingPanParcelPids,
  parcelsOutsideLocalGovernment,
  mergeRecordParcels,
} from '@munifocus/contracts-gov';
import { recordOpenedFile, useContactEntry } from '../lib/front-desk-api';
import { AppShell } from '../components/AppShell';
import { PageIntro } from '../components/PageIntro';
import { FileFormFields, needsSpecify } from '../components/file-form/FileFormFields';
import {
  fileFormGateMessages,
  EMPTY_FILE_FORM,
  useFileForm,
  type FileFormValues,
} from '../components/file-form/useFileForm';
import { useSubmitValidation } from '../lib/useSubmitValidation';
import { SnbUploadPanel, type RestoredUpload } from '../components/tombstone/SnbUploadPanel';
import { SnbSourceGuide } from '../components/tombstone/SnbSourceGuide';
import { IntakeChecks, type IntakeCheckAnswer } from '../components/tombstone/IntakeChecks';
import { useLocalGovernments } from '../lib/local-governments-api';
import { extractStaged } from '../lib/property-extraction-api';
import { useComplaint, useIntakeFile } from '../lib/files-api';
import { intakeDraftKey, useSessionDraft, useSessionDraftSync } from '../lib/session-draft';

/**
 * Everything an unsubmitted intake consists of.
 *
 * The staged SNB uploads are part of it, not just the typed fields. Their rows
 * live server-side under `staging/` and outlive a reload perfectly well, so
 * restoring the values without them would leave the officer looking at a filled
 * form whose supporting records silently would not attach on submit.
 */
interface IntakeDraft {
  values: FileFormValues;
  staged: StagedRecord[];
  stagedDrafts: Record<string, TombstoneDraft>;
  mismatches: ExtractionMismatch[];
  /** Reasons typed against the Stage 1 checks. Optional: older drafts lack it. */
  intakeAnswers?: Record<string, IntakeCheckAnswer>;
}

interface StagedRecord {
  id: string;
  kind: SnbDocumentKind;
  pid?: string | null;
  panPids?: string[];
  /**
   * Where a Parcel record places the land, for the check that it lies inside
   * the file's local government. Never read off a PAN: that is where the tax
   * bill goes, not where the land is.
   */
  placeName?: string | null;
  taxingAuthority?: string | null;
  parish?: string | null;
  /**
   * The receipt the upload panel showed for this record.
   *
   * Kept here because the panel's own copy is local state that a reload wipes:
   * without it the officer came back to a bare "done" chip that named no file,
   * did not say a second Parcel record had been uploaded, and — worse — carried
   * no discard control, so the only way out of a wrong PDF was to start over and
   * lose the whole form. Optional: a draft written before this was stored still
   * restores, it just falls back to the chip.
   */
  fileName?: string;
  fieldsRead?: number;
  warnings?: string[];
}

/**
 * Fields the form can fill in on its own, with no officer involvement.
 *
 * `complainantLocale` follows the interface language, and `localGovernmentId` is
 * pre-selected by `LocalGovernmentPicker` when the officer holds exactly one
 * appointment. Neither is evidence that anyone typed anything, so neither can
 * count towards "is there work here to restore".
 */
const SELF_POPULATING_FIELDS = ['complainantLocale', 'localGovernmentId'] as const;

/**
 * A stored draft, if it is one — parsed from sessionStorage and therefore not to
 * be trusted.
 *
 * The entry may predate a change to `IntakeDraft`, or have been written by an
 * older build, or truncated. Reading it optimistically means intake crashes on
 * mount for anyone holding a stale entry, and the officer cannot get past it by
 * reloading — which is precisely the situation this feature exists to prevent.
 */
function isIntakeDraft(value: unknown): value is IntakeDraft {
  if (typeof value !== 'object' || value === null) return false;
  const draft = value as Partial<IntakeDraft>;
  return (
    typeof draft.values === 'object' &&
    draft.values !== null &&
    Array.isArray(draft.staged) &&
    Array.isArray(draft.mismatches) &&
    typeof draft.stagedDrafts === 'object' &&
    draft.stagedDrafts !== null
  );
}

/**
 * Whether a stored draft holds anything worth restoring.
 *
 * A draft is written on every change, so simply opening the screen and leaving
 * stores a form nobody touched. Restoring that is indistinguishable from a fresh
 * form — except that it would announce itself, telling the officer their work
 * was recovered when there was none.
 */
function hasContent(draft: unknown): draft is IntakeDraft {
  if (!isIntakeDraft(draft)) return false;
  if (draft.staged.length > 0) return true;

  const untouched: FileFormValues = { ...EMPTY_FILE_FORM };
  for (const field of SELF_POPULATING_FIELDS) {
    untouched[field] = draft.values[field] as never;
  }
  return JSON.stringify(draft.values) !== JSON.stringify(untouched);
}

/**
 * The recovered uploads as the panel's own receipts.
 *
 * Every field is checked rather than trusted, for the same reason `isIntakeDraft`
 * checks the draft around it: this comes from sessionStorage, so it may have been
 * written by an older build or truncated mid-write. It is also the first thing to
 * read the *elements* of `staged` — `isIntakeDraft` only establishes that it is an
 * array — and a receipt of the wrong shape throws while the panel renders. That is
 * the worst failure available here: intake dies on mount, and the officer's one
 * instinct is to reload, which hits the same entry again.
 *
 * A record whose receipt does not survive the check is dropped rather than
 * repaired, falling back to the "read" chip — the same graceful path a draft
 * written before receipts existed already takes. The upload still attaches on
 * submit either way; only its row is lost.
 */
function toRestoredUploads(staged: StagedRecord[] = []): RestoredUpload[] {
  return staged.flatMap((s) => {
    if (typeof s?.id !== 'string' || typeof s.fileName !== 'string' || s.fileName === '') return [];
    return [
      {
        ref: s.id,
        kind: s.kind,
        fileName: s.fileName,
        // A count that is not a number would reach i18n's plural selection, which
        // picks a form off `count` and has no answer for "9".
        fieldsRead:
          typeof s.fieldsRead === 'number' && Number.isFinite(s.fieldsRead) ? s.fieldsRead : 0,
        // The receipt maps over this. `?? []` covers null and undefined but not a
        // string, which is the shape that actually throws.
        warnings: Array.isArray(s.warnings) ? s.warnings.filter((w) => typeof w === 'string') : [],
      },
    ];
  });
}

export function NewFile() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const intake = useIntakeFile();

  /**
   * Accepting a report from the public triage queue.
   *
   * The complaint already exists — written by the person who made the report —
   * so intake opens a file against it rather than creating a second one. In the
   * URL rather than router state so a reload does not silently turn an
   * acceptance into a fresh, unlinked file that leaves the report untriaged.
   */
  const [searchParams] = useSearchParams();
  const acceptingComplaintId = searchParams.get('complaintId');
  const acceptedReport = useComplaint(acceptingComplaintId);
  // Opening a file from a front-desk log entry. In the URL for the same reason:
  // a reload must keep the link back to the entry.
  const contactLogId = acceptingComplaintId ? null : searchParams.get('contactLogId');
  const contactEntry = useContactEntry(contactLogId);

  // One draft per source: accepting a report must never restore a manual
  // intake typed earlier in the session over the report's own words.
  const draft = useSessionDraft<IntakeDraft>(
    intakeDraftKey({ complaintId: acceptingComplaintId, contactLogId }),
  );
  const recovered = hasContent(draft.restored) ? draft.restored : null;
  // Nothing worth keeping, or nothing this build understands — drop it so a
  // stale entry cannot outlive the session that wrote it.
  if (draft.restored && !recovered) draft.clear();
  const [showRestored, setShowRestored] = useState(recovered !== null);

  const emptyForm = useMemo<FileFormValues>(
    () => ({
      ...EMPTY_FILE_FORM,
      // Default to the officer's current language — a better first guess than a
      // blind English default in a French municipality. The officer can override.
      complainantLocale: i18n.language?.startsWith('fr') ? 'fr' : 'en',
    }),
    [i18n.language],
  );

  // Restored values open the form, but `dirty` is still measured against the
  // empty one: recovered work is unsaved work, and leaving would lose it.
  //
  // Layered over the empty form rather than used as-is: a draft written by an
  // older build can be missing a field this one renders, and handing React an
  // `undefined` value turns a controlled input into an uncontrolled one.
  const form = useFileForm(
    recovered ? { ...emptyForm, ...recovered.values } : emptyForm,
    emptyForm,
  );
  const { values } = form;

  /**
   * Carry an accepted report's own words into the form.
   *
   * Address and description only — the rest is the municipality's work to do
   * from its own records, and a member of the public could not have supplied
   * it. Applied once, and never over anything the officer has typed: a slow
   * fetch resolving after they started editing must not overwrite them.
   */
  const report = acceptedReport.data;
  const [reportApplied, setReportApplied] = useState(false);
  useEffect(() => {
    if (!report || reportApplied) return;
    setReportApplied(true);
    if (!values.address) form.set('address', report.subjectAddress);
    if (!values.description) form.set('description', report.description);
    // The report's own intake method (a citizen report is `online_report`) is
    // what the file is opened under; showing it keeps the form honest.
    if (!values.intakeMethod && report.intakeMethod) form.set('intakeMethod', report.intakeMethod);
    // Who reported it. The file is opened against the existing complaint, so
    // nothing typed into these fields is ever sent — they used to open empty and
    // editable, inviting the officer to re-key the reporter into a form that
    // then discarded it. Shown as reported, and locked (see `complaintLocked`).
    form.set('anonymous', report.anonymous);
    form.set('complainantName', report.complainantName ?? '');
    form.set('complainantEmail', report.complainantEmail ?? '');
    form.set('complainantPhone', report.complainantPhone ?? '');
    form.set('complainantAddress', report.complainantAddress ?? '');
    form.set('complainantCity', report.complainantCity ?? '');
    form.set('complainantProvince', report.complainantProvince ?? '');
    form.set('complainantPostalCode', report.complainantPostalCode ?? '');
    if (report.complainantRelationship) {
      form.set('complainantRelationship', report.complainantRelationship);
      form.set('complainantRelationshipOther', report.complainantRelationshipOther ?? '');
    }
    if (report.complainantLocale) form.set('complainantLocale', report.complainantLocale);
    // A citizen report's category is the resident's guess at the by-law; it is
    // a starting point the officer can change, never a classification.
    if (report.citizenReport) {
      form.set('bylawCategory', bylawCategoryForReport(report.citizenReport.category));
    }
  }, [report, reportApplied, values.address, values.description, values.intakeMethod, form]);

  /**
   * Carry a front-desk entry into the form, once and never over typed values:
   * the property, the notes as the description, the channel as the intake
   * method, and the caller as the complainant (anonymity included).
   */
  const contact = contactEntry.data;
  const [contactApplied, setContactApplied] = useState(false);
  useEffect(() => {
    if (!contact || contactApplied) return;
    setContactApplied(true);
    if (!values.address && contact.address) form.set('address', contact.address);
    if (!values.description && contact.notes) form.set('description', contact.notes);
    if (!values.intakeMethod) {
      form.set('intakeMethod', CONTACT_CHANNEL_INTAKE_METHOD[contact.channel]);
    }
    if (!contact.redacted) {
      if (!values.complainantName && contact.callerName) {
        form.set('complainantName', contact.callerName);
      }
      if (!values.complainantPhone && contact.callerPhone) {
        form.set('complainantPhone', contact.callerPhone);
      }
      if (!values.complainantEmail && contact.callerEmail) {
        form.set('complainantEmail', contact.callerEmail);
      }
      if (contact.anonymous) form.set('anonymous', true);
    }
  }, [
    contact,
    contactApplied,
    values.address,
    values.description,
    values.intakeMethod,
    values.complainantName,
    values.complainantPhone,
    values.complainantEmail,
    form,
  ]);

  // SNB records extracted before the file exists. Their uploads sit under
  // `staging/` until `POST /files` claims them; the ids ride along on that call.
  // Staged uploads, kind included: discarding one has to detach exactly the
  // records it produced, and a re-upload of the same kind stages another id.
  //
  // Each entry also carries what the record identified — a Parcel's own PID, a
  // PAN's full PID list — so the "missing Parcel record" warning is *derived*
  // from what is currently staged rather than latched at upload time. Latching
  // it meant discarding a parcel hid the warning instead of widening it.
  const [staged, setStaged] = useState<StagedRecord[]>(recovered?.staged ?? []);
  // Mirror of the staged ids that updates synchronously.
  //
  // Selecting two Parcel records at once uploads them in one pass, and the
  // second call is made before React has re-rendered — so a state-derived id
  // list would still be the pre-upload one, and the second extraction would be
  // cross-checked without the first. The ref is what the API call reads.
  const stagingIdsRef = useRef<string[]>(recovered?.staged.map((s) => s.id) ?? []);
  // What each record proposed, kept so discarding it can offer to take back the
  // values it filled — and only those the officer has not since changed.
  //
  // Keyed by staging id rather than by kind: a straddled property has one Parcel
  // record per PID, and keying by kind meant the second upload overwrote the
  // first's draft, so discarding either offered to clear the wrong values.
  const [stagedDrafts, setStagedDrafts] = useState<Record<string, TombstoneDraft>>(
    recovered?.stagedDrafts ?? {},
  );
  const stagingIds = useMemo(() => staged.map((s) => s.id), [staged]);
  const stagedExtractions = useMemo(() => [...new Set(staged.map((s) => s.kind))], [staged]);
  // The recovered uploads as receipts, so the panel opens showing the file names
  // and a discard control per record instead of one anonymous "read" chip.
  //
  // State rather than derived from `staged`: the panel seeds itself once and owns
  // its rows from then on, so handing it the live list would fight its own
  // discards. That also means only a fresh mount can clear those rows — hence
  // `uploadPanelKey`, which `startOver` bumps.
  const [restoredUploads, setRestoredUploads] = useState<RestoredUpload[]>(() =>
    toRestoredUploads(recovered?.staged),
  );
  const [uploadPanelKey, setUploadPanelKey] = useState(0);
  // Conflicts between the two records, recomputed on each upload. Surfaced here
  // and not only on the tombstone: this is the screen where a mismatched pair of
  // documents actually gets uploaded.
  const [mismatches, setMismatches] = useState<ExtractionMismatch[]>(recovered?.mismatches ?? []);

  // One snapshot of everything unsubmitted, rewritten whenever any part changes.
  // Answers to the Stage 1 checks, keyed by `kind:pid`. Kept for findings that
  // have since cleared, so a reason is not lost if the same one comes back.
  const [intakeAnswers, setIntakeAnswers] = useState<Record<string, IntakeCheckAnswer>>(
    recovered?.intakeAnswers ?? {},
  );

  useSessionDraftSync(
    draft,
    useMemo(
      () => ({ values, staged, stagedDrafts, mismatches, intakeAnswers }),
      [values, staged, stagedDrafts, mismatches, intakeAnswers],
    ),
  );
  // PIDs the PAN enumerated for which no Parcel record is currently staged.
  //
  // Derived, not stored: discarding a parcel has to *widen* this, and the newest
  // PAN wins for the same reason it does in `toTombstoneDraft` — a re-upload is
  // how an officer corrects the wrong record.
  const missingParcelPids = useMemo(() => {
    const panPids = [...staged].reverse().find((s) => s.kind === 'snb_pan')?.panPids ?? [];
    const have = new Set(
      staged
        .filter((s) => s.kind === 'snb_parcel')
        .map((s) => s.pid?.trim())
        .filter((pid): pid is string => !!pid),
    );
    return panPids.map((pid) => pid.trim()).filter((pid) => pid !== '' && !have.has(pid));
  }, [staged]);
  const panPids = useMemo(
    () => [...staged].reverse().find((s) => s.kind === 'snb_pan')?.panPids ?? [],
    [staged],
  );
  const localGovernments = useLocalGovernments();
  const anchor = localGovernments.data?.find((lg) => lg.id === values.localGovernmentId) ?? null;
  // The Stage 1 checks, derived like `missingParcelPids` so fixing the intake —
  // uploading the record, changing the local government, removing the PID —
  // clears a finding without anyone having to dismiss it. The API runs the
  // same two checks and refuses a file that leaves one unanswered.
  const intakeFindings = useMemo<IntakeCheckFinding[]>(() => {
    const parcelRecords = staged.filter((s) => s.kind === 'snb_parcel' && s.pid?.trim());
    // Only the PIDs the file will link: removing one is how a finding is
    // answered, and with none left there is no land here to check.
    const linked = new Set(form.parcels.map((p) => p.pid.trim()).filter(Boolean));
    return [
      ...missingPanParcelPids(
        panPids,
        parcelRecords.map((s) => s.pid as string),
      ).map((pid) => ({ kind: 'pid_not_in_matter' as const, pid })),
      ...parcelsOutsideLocalGovernment(
        parcelRecords
          .filter((s) => linked.has((s.pid as string).trim()))
          .map((s) => ({
            pid: s.pid,
            placeName: s.placeName,
            taxingAuthority: s.taxingAuthority,
            parish: s.parish,
          })),
        anchor,
      ),
    ];
  }, [staged, panPids, form.parcels, anchor]);
  const confirmedFindings = intakeFindings.filter(
    (f) => intakeAnswers[intakeCheckKey(f)]?.confirmed,
  );
  const notInMatter = new Set(
    confirmedFindings.filter((f) => f.kind === 'pid_not_in_matter').map((f) => f.pid),
  );

  // Shown when leaving would throw away entered intake, not on an empty form.
  const [confirmLeave, setConfirmLeave] = useState(false);

  // Leaving from here is a real exit, not a step back in a flow: the intake has
  // no draft on the server, so anything typed goes. Confirm when there is
  // something to lose, and go straight out when there isn't.
  const leave = () => {
    // Discarding means discarding: a recovered draft that survived the officer
    // choosing to throw it away would come straight back on the next intake.
    draft.clear();
    navigate('/');
  };
  const requestLeave = () => (form.dirty || staged.length > 0 ? setConfirmLeave(true) : leave());

  /** Abandon the recovered draft and start from an empty form. */
  const startOver = () => {
    draft.clear();
    form.reset(emptyForm);
    setStaged([]);
    setStagedDrafts({});
    setMismatches([]);
    setIntakeAnswers({});
    stagingIdsRef.current = [];
    // Remount the upload panel. Its rows are seeded on mount and it owns them
    // after that, so without this the recovered receipts would sit above an
    // emptied form naming records the intake no longer holds.
    setRestoredUploads([]);
    setUploadPanelKey((k) => k + 1);
    setShowRestored(false);
    // Starting over an accepted report (or front-desk entry) starts from what
    // it carried, not from a blank form: let the prefill apply again.
    setReportApplied(false);
    setContactApplied(false);
  };

  const v = useSubmitValidation({
    ...fileFormGateMessages(form, t),
    intakeChecks:
      confirmedFindings.length < intakeFindings.length ? t('gov.intakeChecks.required') : null,
  });

  const submit = () => {
    if (!form.canSubmit) return;
    intake.mutate(
      {
        // Present only when accepting a public report; `intakeFile` then skips
        // creating a complaint and opens the file against the existing one.
        existingComplaintId: acceptingComplaintId ?? undefined,
        // `source` is derived server-side from the method so the two can't disagree.
        // Every *Other is gated on its own code: the field unmounts when the
        // officer reclassifies, but the state it held does not, and stale text
        // describing a value the file no longer has must not be sent.
        intakeMethod: values.intakeMethod || undefined,
        intakeMethodOther: needsSpecify(values.intakeMethod) ? values.intakeMethodOther.trim() : '',
        referringPerson: form.showInternalSource ? values.referringPerson.trim() : '',
        internalContext: form.showInternalSource ? values.internalContext.trim() : '',
        // Left empty rather than filled with an English placeholder: the name
        // prints on French forms and greets the complainant by email. The
        // screens render the absence in the reader's language instead.
        complainantName: form.showComplainant ? values.complainantName.trim() : '',
        complainantEmail: form.showComplainant
          ? values.complainantEmail.trim() || undefined
          : undefined,
        // Identity and contact are kept even when anonymous — the flag governs
        // outward-facing output, not what the file retains for follow-up.
        anonymous: form.showComplainant ? values.anonymous : false,
        complainantPhone: form.showComplainant ? values.complainantPhone.trim() : '',
        // The complainant's own address, gated with the rest of §B exactly as
        // the phone is: an internal report has no complainant to have one.
        complainantAddress: form.showComplainant ? values.complainantAddress.trim() : '',
        complainantCity: form.showComplainant ? values.complainantCity.trim() : '',
        complainantProvince: form.showComplainant ? values.complainantProvince.trim() : '',
        complainantPostalCode: form.showComplainant ? values.complainantPostalCode.trim() : '',
        complainantRelationship: form.showComplainant
          ? values.complainantRelationship || undefined
          : undefined,
        complainantRelationshipOther:
          form.showComplainant && needsSpecify(values.complainantRelationship)
            ? values.complainantRelationshipOther.trim()
            : '',
        complainantLocale: values.complainantLocale,
        description: values.description.trim(),
        address: values.address.trim(),
        propertyType: values.propertyType || undefined,
        propertyTypeOther: needsSpecify(values.propertyType) ? values.propertyTypeOther.trim() : '',
        bylawCategory: values.bylawCategory,
        enforcedLawId: values.enforcedLawId || undefined,
        bylawCategoryOther: needsSpecify(values.bylawCategory)
          ? values.bylawCategoryOther.trim()
          : '',
        priority: values.priority,
        // Dropped at routine so a reason picked and then demoted away isn't sent.
        priorityReason: form.showPriorityReason ? values.priorityReason || undefined : undefined,
        priorityReasonOther:
          form.showPriorityReason && needsSpecify(values.priorityReason)
            ? values.priorityReasonOther.trim()
            : '',
        pan: values.pan.trim(),
        county: values.county.trim(),
        // A PID confirmed as not part of this matter is not linked to the file.
        parcels: form.parcels.filter((p) => !notInMatter.has(p.pid.trim())),
        intakeConfirmations: confirmedFindings.length
          ? confirmedFindings.map((f) => ({
              kind: f.kind,
              pid: f.pid,
              reason: intakeAnswers[intakeCheckKey(f)].reason.trim(),
            }))
          : undefined,
        ownerName: values.ownerName.trim(),
        ownerEmail: !values.ownerUnknown ? values.ownerEmail.trim() || undefined : undefined,
        ownerPhone: !values.ownerUnknown ? values.ownerPhone.trim() : '',
        ownerLocale: !values.ownerUnknown ? values.ownerLocale || undefined : undefined,
        ownerType: values.ownerType || undefined,
        ownerTypeOther: needsSpecify(values.ownerType) ? values.ownerTypeOther.trim() : '',
        mailingAddress: values.mailingAddress.trim(),
        harmonizationStatus: values.harmonizationStatus.trim(),
        propertyDescription: values.propertyDescription.trim(),
        taxingAuthority: values.taxingAuthority.trim(),
        postalCode: values.postalCode.trim(),
        taxClass: values.taxClass.trim(),
        neighbourhood: values.neighbourhood.trim(),
        ownerUnknown: values.ownerUnknown,
        // The occupant block. Sent independently of `ownerUnknown` — not
        // knowing who owns a property says nothing about who lives in it.
        occupantName: values.occupantName.trim(),
        occupantPhone: values.occupantPhone.trim(),
        occupantAddress: values.occupantAddress.trim(),
        occupantCity: values.occupantCity.trim(),
        occupantProvince: values.occupantProvince.trim(),
        occupantPostalCode: values.occupantPostalCode.trim(),
        // Omitted rather than sent empty when unanchored: the DTO validates it
        // as a UUID when present, and "no local government" is an absent
        // anchor, not a blank one.
        localGovernmentId: values.localGovernmentId,
        assignedOfficerId: values.assignedOfficerId || undefined,
        extractionStagingIds: stagingIds,
      },
      {
        onSuccess: async (file) => {
          // Link the front-desk entry this intake came from. Best effort: the
          // file exists either way, and the clerk can attach it from the log.
          if (contactLogId) {
            try {
              await recordOpenedFile(contactLogId, file.id);
            } catch {
              // Not worth failing the navigation over.
            }
          }
          // The draft has served its purpose — the file exists now, and leaving
          // it behind would offer to restore this intake into the next one.
          draft.clear();
          // The officer lands on a file they have not seen before; without this
          // the only evidence the submit worked is that the form went away.
          snackbar.success(t('gov.file.createdToast', { reference: file.reference }));
          navigate(`/files/${file.id}`);
        },
      },
    );
  };

  /**
   * The tombstone fields a record filled, paired with their live value.
   *
   * Provenance by value: if the field still reads exactly what the record
   * proposed, the record is the only thing that could have put it there. If the
   * officer has since changed it, it no longer matches and is left alone.
   */
  const draftBindings = (draft: TombstoneDraft) =>
    [
      ['address', draft.address],
      ['pan', draft.pan],
      ['county', draft.county],
      ['ownerName', draft.ownerName],
      ['mailingAddress', draft.mailingAddress],
      ['harmonizationStatus', draft.harmonizationStatus],
      ['propertyDescription', draft.propertyDescription],
      ['taxingAuthority', draft.taxingAuthority],
      ['postalCode', draft.postalCode],
      ['taxClass', draft.taxClass],
      ['neighbourhood', draft.neighbourhood],
    ] as const;

  const parcelsStillFromDraft = (draft: TombstoneDraft) =>
    draft.parcels.length > 0 && JSON.stringify(values.parcelRows) === JSON.stringify(draft.parcels);

  function valuesStillFromDraft(draft: TombstoneDraft): number {
    const fields = draftBindings(draft).filter(
      ([key, proposed]) => proposed != null && proposed !== '' && values[key] === proposed,
    ).length;
    const owner = draft.ownerType != null && values.ownerType === draft.ownerType ? 1 : 0;
    return fields + owner + (parcelsStillFromDraft(draft) ? draft.parcels.length : 0);
  }

  function clearValuesFromDraft(draft: TombstoneDraft): void {
    for (const [key, proposed] of draftBindings(draft)) {
      if (proposed != null && proposed !== '' && values[key] === proposed) form.set(key, '');
    }
    if (draft.ownerType != null && values.ownerType === draft.ownerType) form.set('ownerType', '');
    if (parcelsStillFromDraft(draft)) form.setParcelRows([{ pid: '', isPrimary: true }]);
  }

  return (
    <AppShell
      back={{ label: t('gov.nav.files'), onClick: requestLeave }}
      bottomBar={
        <Stack spacing={1}>
          {/* Beside the button that was pressed: focus has moved to the first
              field that needs an answer, which may be screens away. */}
          {v.invalidCount > 0 && (
            <Alert severity="error" role="alert">
              {t('gov.file.fixBeforeSubmit', { count: v.invalidCount })}
            </Alert>
          )}
          <Stack direction="row" spacing={1}>
            <Button fullWidth variant="outlined" disabled={intake.isPending} onClick={requestLeave}>
              {t('common.actions.cancel')}
            </Button>
            <Button
              fullWidth
              variant="contained"
              disabled={intake.isPending}
              onClick={v.guard(submit)}
            >
              {intake.isPending ? t('gov.file.creating') : t('common.actions.submit')}
            </Button>
          </Stack>
        </Stack>
      }
    >
      {/* The topbar carries the title and the way out; this says what the screen
          is for, which is the one thing a bar has no room to say. */}
      <PageIntro>{t('gov.file.newSubtitle')}</PageIntro>

      {/* Say so, rather than quietly presenting a filled form: the officer needs
          to know these values came from an earlier session, not from the caller
          they are on the phone with now. */}
      {showRestored && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={startOver}>
              {t('gov.intake.restored.startOver')}
            </Button>
          }
          onClose={() => setShowRestored(false)}
        >
          {t('gov.intake.restored.body')}
        </Alert>
      )}

      {intake.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('gov.file.createError')}
        </Alert>
      )}

      <FileFormFields
        form={form}
        validation={v}
        autoSelectSoleLocalGovernment
        // Accepting a public report: the complaint already exists and is what
        // the file opens against, so what was reported is shown, not re-taken.
        complaintLocked={Boolean(acceptingComplaintId)}
        snb={
          /* Optional: read the fields off an SNB record instead of typing them.
             The officer reviews and edits everything below either way. Bounded as
             one card so the upload actions read as a detour off the form rather
             than the first two questions of it. */
          <HairlineCard sx={{ p: { xs: 2, md: 2.5 } }}>
            <SnbUploadPanel
              key={uploadPanelKey}
              done={stagedExtractions}
              restored={restoredUploads}
              onDiscard={(kind, ref) => {
                // Detach exactly the upload being discarded so it is not attached
                // to the file on submit. Without a ref (a PAN, which is one per
                // file) fall back to dropping that kind.
                setStaged((prev) => prev.filter((s) => (ref ? s.id !== ref : s.kind !== kind)));
                const draftKey = ref ?? staged.find((s) => s.kind === kind)?.id;
                stagingIdsRef.current = stagingIdsRef.current.filter((id) =>
                  draftKey ? id !== draftKey : true,
                );
                setStagedDrafts((prev) => {
                  if (!draftKey) return prev;
                  const next = { ...prev };
                  delete next[draftKey];
                  return next;
                });
                // `missingParcelPids` is derived from `staged`, so it widens on
                // its own — clearing it here would hide a warning that just
                // became more true.
                setMismatches([]);

                // Values it wrote into the form stay by default: by now they are
                // indistinguishable from typed input, and blanking a field the
                // officer corrected is worse than leaving one to clear. Offer the
                // cleanup instead of doing it, and only when there is one to do.
                const draft = draftKey ? stagedDrafts[draftKey] : undefined;
                const untouched = draft ? valuesStillFromDraft(draft) : 0;
                if (untouched === 0) {
                  snackbar.info(t('gov.extraction.upload.discarded'));
                  return;
                }
                snackbar.info(t('gov.extraction.upload.discardedKept', { count: untouched }), {
                  action: {
                    label: t('gov.extraction.upload.clearThem'),
                    onClick: () => clearValuesFromDraft(draft as TombstoneDraft),
                  },
                });
              }}
              onUpload={async (document, kind) => {
                // Read the ids from the ref, not from state: uploading two Parcel
                // records in one selection runs both calls before React re-renders,
                // and the second must still be cross-checked against the first.
                const result = await extractStaged(document, kind, stagingIdsRef.current);
                const d = result.draft;
                stagingIdsRef.current = [...stagingIdsRef.current, result.stagingId];
                const extracted = result.run.extracted;
                const fieldsRead = countExtractedFields(extracted);
                setStaged((prev) => [
                  ...prev,
                  {
                    id: result.stagingId,
                    kind,
                    pid: extracted?.kind === 'snb_parcel' ? extracted.fields.pid : null,
                    panPids: extracted?.kind === 'snb_pan' ? extracted.fields.pids : undefined,
                    ...(extracted?.kind === 'snb_parcel'
                      ? {
                          placeName: extracted.fields.placeName,
                          taxingAuthority: extracted.fields.taxingAuthority,
                          parish: extracted.fields.parish,
                        }
                      : {}),
                    // Everything the panel's receipt shows, so a reload can put the
                    // row back rather than reduce it to "done".
                    fileName: document.name,
                    fieldsRead,
                    warnings: result.run.warnings,
                  },
                ]);
                setStagedDrafts((prev) => ({ ...prev, [result.stagingId]: d }));
                setMismatches(d.mismatches);
                // Fill blanks only — anything the officer already typed was entered
                // by a person and must not be overwritten by a document.
                form.fillIfBlank('address', d.address);
                form.fillIfBlank('pan', d.pan);
                form.fillIfBlank('county', d.county);
                form.fillIfBlank('ownerName', d.ownerName);
                form.fillIfBlank('ownerType', (d.ownerType ?? '') as OwnerType);
                form.fillIfBlank('mailingAddress', d.mailingAddress);
                form.fillIfBlank('harmonizationStatus', d.harmonizationStatus);
                form.fillIfBlank('propertyDescription', d.propertyDescription);
                form.fillIfBlank('taxingAuthority', d.taxingAuthority);
                form.fillIfBlank('postalCode', d.postalCode);
                form.fillIfBlank('taxClass', d.taxClass);
                form.fillIfBlank('neighbourhood', d.neighbourhood);
                // Every PID on the PAN, so a straddled property arrives with both
                // parcels — added beside any PID already typed, never instead of it.
                if (d.parcels.length > 0) {
                  form.setParcelRows(mergeRecordParcels(values.parcelRows, d.parcels));
                }
                // Report back what came off the document, so a PDF that is not an
                // SNB record says so instead of looking like a clean upload.
                return { fieldsRead, warnings: result.run.warnings, ref: result.stagingId };
              }}
            />

            {/* One PAN covers every parcel on the account, so a building
                straddling two lots is one PAN and two PIDs — each needing its own
                Parcel record. Missing one is otherwise silent. */}
            {/* With several PIDs on the PAN this becomes a check that has to be
                answered (below); the reminder is for the one-PID case. */}
            {missingParcelPids.length > 0 && panPids.length < 2 && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                {t('gov.extraction.upload.missingParcels', {
                  count: missingParcelPids.length,
                  pids: missingParcelPids.join(', '),
                })}{' '}
                {t('gov.extraction.upload.missingParcelsHint')}
              </Alert>
            )}

            <IntakeChecks
              findings={intakeFindings}
              answers={intakeAnswers}
              onAnswer={(key, answer) => setIntakeAnswers((prev) => ({ ...prev, [key]: answer }))}
              pan={values.pan.trim()}
              panPids={panPids}
              localGovernmentName={anchor?.name ?? ''}
              error={v.errors.intakeChecks}
              inputRef={v.register('intakeChecks')}
            />

            <Divider sx={{ my: 1.5 }} />

            <SnbSourceGuide extracted={stagedExtractions} mismatches={mismatches} />
          </HairlineCard>
        }
      />

      <Dialog open={confirmLeave} onClose={() => setConfirmLeave(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('gov.intake.leave.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('gov.intake.leave.body')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmLeave(false)}>{t('gov.intake.leave.stay')}</Button>
          <Button color="error" onClick={leave}>
            {t('gov.intake.leave.discard')}
          </Button>
        </DialogActions>
      </Dialog>
    </AppShell>
  );
}
